import React, { useState, useEffect, useMemo, useCallback, lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import TopProgressBar from './components/TopProgressBar';
import { AppErrorBoundary, DevErrorBoundary } from './components/ErrorBoundary';
import { db, doc, getDoc, updateDoc, cachedGetDoc, onSnapshot } from './services/firebase';
import MaintenancePage from './components/common/MaintenancePage';
import { ConfirmProvider } from './components/ConfirmModal';
import { FeatureFlagProvider } from './context/FeatureFlags';
import { initErrorTracking } from './services/errorLogger';
import { runAutoCleanup } from './utils/autoCleanup';
import { initUsageTracking } from './utils/usageTracker';
import SplashScreen from './components/SplashScreen';
import InstallPrompt from './components/InstallPrompt';
import OfflineBanner from './components/OfflineBanner';
import WelcomePopup from './components/WelcomePopup';
import PaymentBlock from './components/PaymentBlock';
import DeploymentDisabled, { IS_VERCEL_BLOCK_ACTIVE } from './components/DeploymentDisabled';

import Login from './pages/Login';

// Login success animation overlay
function LoginSuccess({ name, role }) {
  return (
    <div className="fixed inset-0 z-[999] bg-[#0c1524] flex flex-col items-center justify-center p-6 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-[#1e3a5f]/35 via-[#0c1524] to-[#080d16]">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 240, damping: 20 }}
        className="flex flex-col items-center gap-6 max-w-xs text-center"
      >
        {/* Animated Check badge with aura */}
        <div className="relative">
          {/* Subtle pulse ripple */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0.5 }}
            animate={{ scale: 1.8, opacity: 0 }}
            transition={{ delay: 0.15, duration: 1, ease: 'easeOut' }}
            className="absolute inset-0 rounded-2xl bg-[#3b82f6]/40"
          />

          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: [0, 1.12, 1] }}
            transition={{ duration: 0.45, times: [0, 0.7, 1] }}
            className="w-20 h-20 rounded-2xl bg-[#1e3a5f] border border-[#60a5fa]/30 flex items-center justify-center shadow-xl shadow-[#1e3a5f]/50 relative z-10"
          >
            <motion.svg
              width="36"
              height="36"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <motion.path
                d="M5 13l4 4L19 7"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ delay: 0.25, duration: 0.4 }}
              />
            </motion.svg>
          </motion.div>
        </div>

        {/* Text */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="space-y-1.5"
        >
          <span className="inline-block text-[10px] font-bold uppercase tracking-wider bg-white/10 text-blue-200 px-2.5 py-0.5 rounded-full border border-white/15">
            {role === 'admin' ? 'Admin Portal' : 'Retailer Portal'}
          </span>
          <p className="text-white font-extrabold text-2xl tracking-tight">
            {name ? `Welcome, ${name}` : 'Welcome back'}
          </p>
          <p className="text-[#94a3b8] text-xs">
            Authentication verified · Loading workspace...
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
}

// Lazy load pages for faster initial load
const RetailerHome = lazy(() => import('./pages/retailer/Home'));
const PlaceOrder = lazy(() => import('./pages/retailer/PlaceOrder'));
const Checkout = lazy(() => import('./pages/retailer/Checkout'));
const TrackOrder = lazy(() => import('./pages/retailer/TrackOrder'));
const OrderHistory = lazy(() => import('./pages/retailer/OrderHistory'));
const MyLedger = lazy(() => import('./pages/retailer/MyLedger'));
const RetailerProfile = lazy(() => import('./pages/retailer/Profile'));
const Support = lazy(() => import('./pages/retailer/Support'));
const Settings = lazy(() => import('./pages/retailer/Settings'));
const PriceList = lazy(() => import('./pages/retailer/PriceList'));
const About = lazy(() => import('./pages/retailer/About'));
const PrivacyPolicy = lazy(() => import('./pages/retailer/PrivacyPolicy'));
const Terms = lazy(() => import('./pages/retailer/Terms'));
const Blocked = lazy(() => import('./pages/retailer/Blocked'));
const LandingPage = lazy(() => import('./pages/public/LandingPage'));

const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminRetailers = lazy(() => import('./pages/admin/Retailers'));
const OrderDetail = lazy(() => import('./pages/admin/OrderDetail'));
const AdminInventory = lazy(() => import('./pages/admin/Inventory'));
const AdminLedger = lazy(() => import('./pages/admin/Ledger'));
const AdminSettings = lazy(() => import('./pages/admin/Settings'));

const DailyLedger = lazy(() => import('./pages/admin/DailyLedger'));
const CompanyOrder = lazy(() => import('./pages/admin/CompanyOrder'));
const SupportTickets = lazy(() => import('./pages/admin/SupportTickets'));
const AdminSessions = lazy(() => import('./pages/admin/Sessions'));
const AdminAnnouncements = lazy(() => import('./pages/admin/Announcements'));
const AdminPlaceOrder = lazy(() => import('./pages/admin/PlaceOrder'));
const DevPanel = lazy(() => import('./pages/admin/DevPanel'));
const ErrorLogs = lazy(() => import('./pages/dev/ErrorLogs'));
const UserSessions = lazy(() => import('./pages/dev/UserSessions'));
const OrderAnalytics = lazy(() => import('./pages/dev/OrderAnalytics'));
const OrderManager = lazy(() => import('./pages/dev/OrderManager'));
const RetailerActivity = lazy(() => import('./pages/dev/RetailerActivity'));
const DeployInfo = lazy(() => import('./pages/dev/DeployInfo'));
const Announcements = lazy(() => import('./pages/dev/Announcements'));
const DatabaseCleanup = lazy(() => import('./pages/dev/DatabaseCleanup'));
const FeatureFlags = lazy(() => import('./pages/dev/FeatureFlags'));
const AuditLog = lazy(() => import('./pages/dev/AuditLog'));
const AppRatings = lazy(() => import('./pages/dev/AppRatings'));
const DataExport = lazy(() => import('./pages/dev/DataExport'));
const BackupRestore = lazy(() => import('./pages/dev/BackupRestore'));
const ApiResponseMonitor = lazy(() => import('./pages/dev/ApiResponseMonitor'));
const ScheduledTasks = lazy(() => import('./pages/dev/ScheduledTasks'));
const ConfigDiff = lazy(() => import('./pages/dev/ConfigDiff'));
const DocEditor = lazy(() => import('./pages/dev/DocEditor'));
const BulkUpdate = lazy(() => import('./pages/dev/BulkUpdate'));
const MonthlyReport = lazy(() => import('./pages/dev/MonthlyReport'));
const DevGuide = lazy(() => import('./pages/dev/DevGuide'));
const NotFound = lazy(() => import('./pages/NotFound'));

import RetailerLayout from './components/layout/RetailerLayout';
import AdminLayout from './components/layout/AdminLayout';
import DeveloperLayout from './components/layout/DeveloperLayout';

import { LazyMotion, domAnimation, m, motion } from 'framer-motion';

// Init global error tracking only if site is running normally
const _isBypassedEarly = typeof window !== 'undefined' && (
  new URLSearchParams(window.location.search).get('bypass') === 'dev' ||
  new URLSearchParams(window.location.search).get('dev') === 'true'
);
if (!IS_VERCEL_BLOCK_ACTIVE || _isBypassedEarly) {
  initErrorTracking();
  runAutoCleanup();
  initUsageTracking();
}

function parseDevice(ua) {
  if (!ua) return 'Unknown';
  if (ua.includes('iPhone')) return 'iPhone';
  if (ua.includes('iPad')) return 'iPad';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('Windows')) return 'Windows PC';
  if (ua.includes('Mac')) return 'Mac';
  if (ua.includes('Linux')) return 'Linux';
  return 'Unknown';
}

function AppContent() {
  const DEV_PHONE = '8051725780';

  // Check if developer is accessing: /dev route, ?bypass=dev, ?dev=true, or logged in as DEV_PHONE
  const isDevAccess = typeof window !== 'undefined' && (
    window.location.pathname.startsWith('/dev') ||
    window.location.pathname.startsWith('/admin') ||
    new URLSearchParams(window.location.search).get('bypass') === 'dev' ||
    new URLSearchParams(window.location.search).get('dev') === 'true' ||
    sessionStorage.getItem('lg_active_phone') === DEV_PHONE ||
    (() => {
      try {
        const u = JSON.parse(localStorage.getItem('lg_user') || '{}');
        return u.phone === DEV_PHONE;
      } catch (e) {
        return false;
      }
    })()
  );

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [splashDone, setSplashDone] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(null); // { name, role }
  const [maintenance, setMaintenance] = useState(false);
  const [maintenanceInfo, setMaintenanceInfo] = useState({
    message: '',
    estimatedUptime: '',
    shopPhone: '9939079107',
  });
  const [siteBlock, setSiteBlock] = useState(() => {
    try {
      if (typeof window !== 'undefined' && localStorage.getItem('lg_site_blocked') === 'true') {
        return { enabled: true, type: 'vercel', errorType: '402' };
      }
    } catch (e) {}
    return null;
  });

  // Minimum splash duration — longer during launch week for premium feel
  useEffect(() => {
    const launch = new Date('2026-06-01T00:00:00');
    const diff = (new Date() - launch) / (1000 * 60 * 60 * 24);
    const isLaunch = diff >= 0 && diff <= 7;
    const timer = setTimeout(() => setSplashDone(true), isLaunch ? 3200 : 1500);
    return () => clearTimeout(timer);
  }, []);

  // Multi-tab instant sync in the same browser
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'lg_site_blocked') {
        if (e.newValue === 'true') {
          setSiteBlock({ enabled: true, type: 'vercel', errorType: '402' });
        } else {
          setSiteBlock(null);
        }
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Real-time synchronization for instant maintenance lockdown & recovery
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'settings', 'app'), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        const isBlockActive = Boolean(data.siteBlock?.enabled);
        setSiteBlock(isBlockActive ? data.siteBlock : null);
        try {
          if (isBlockActive) {
            localStorage.setItem('lg_site_blocked', 'true');
          } else {
            localStorage.setItem('lg_site_blocked', 'false');
          }
        } catch (e) {}
        setMaintenanceInfo({
          message: data.maintenanceMessage || '',
          estimatedUptime: data.maintenanceEstimatedUptime || '',
          shopPhone: data.shopPhone || '9939079107',
        });
      }
    }, (err) => {
      console.error('Settings realtime listener error:', err);
    });
    return () => unsub();
  }, []);

  const verifyUser = useCallback(async () => {
    // Per-tab session isolation: use sessionStorage to know which phone is active in THIS tab
    const activePhone = sessionStorage.getItem('lg_active_phone');
    const saved = activePhone
      ? localStorage.getItem(`lg_user_${activePhone}`)
      : localStorage.getItem('lg_last_login') ? localStorage.getItem(`lg_user_${localStorage.getItem('lg_last_login')}`) : null;
    
    // Backward compat: if no per-user key found, try old key and migrate
    const finalSaved = saved || localStorage.getItem('lg_user');
    if (!finalSaved) { setLoading(false); return; }

    try {
      const parsed = JSON.parse(finalSaved);
      if (!parsed.phone) { setLoading(false); return; }

      // Dev bypass - skip Firestore verification
      if (parsed.phone === DEV_PHONE) {
        if (parsed.sessionExpiry && new Date(parsed.sessionExpiry) < new Date()) {
          localStorage.removeItem(`lg_user_${parsed.phone}`); localStorage.removeItem('lg_user'); sessionStorage.removeItem('lg_active_phone'); setLoading(false); return;
        }
        sessionStorage.setItem('lg_active_phone', parsed.phone);
        localStorage.setItem('lg_user', JSON.stringify(parsed)); // backward compat
        setUser(parsed); setLoading(false); return;
      }

      // Session verify cache - 1 hour tak no DB call (saves reads on rapid login/logout)
      const lastVerify = parseInt(localStorage.getItem('lg_last_verify_' + parsed.phone) || '0');
      const VERIFY_TTL = 60 * 60 * 1000; // 1 hour
      if (Date.now() - lastVerify < VERIFY_TTL && parsed.sessionExpiry && new Date(parsed.sessionExpiry) > new Date()) {
        sessionStorage.setItem('lg_active_phone', parsed.phone);
        setUser(parsed); setLoading(false); return;
      }
      const userDoc = await getDoc(doc(db, 'users', parsed.phone));
      if (!userDoc.exists()) { localStorage.removeItem(`lg_user_${parsed.phone}`); localStorage.removeItem('lg_user'); sessionStorage.removeItem('lg_active_phone'); setLoading(false); return; }

      const firestoreData = userDoc.data();

      // Session validation (skip for admin — multi-device allowed)
      if (firestoreData.role !== 'admin' && parsed.sessionId && firestoreData.activeSession !== parsed.sessionId) {
        // Session terminated from another device
        localStorage.removeItem(`lg_user_${parsed.phone}`);
        localStorage.removeItem('lg_user');
        sessionStorage.removeItem('lg_active_phone');
        setLoading(false);
        return;
      }

      // Session expiry check
      if (parsed.sessionExpiry && new Date(parsed.sessionExpiry) < new Date()) {
        localStorage.removeItem(`lg_user_${parsed.phone}`);
        localStorage.removeItem('lg_user');
        sessionStorage.removeItem('lg_active_phone');
        setLoading(false);
        return;
      }

      const verifiedUser = { ...firestoreData, phone: parsed.phone, sessionId: parsed.sessionId, sessionExpiry: parsed.sessionExpiry, blocked: firestoreData.blocked || false };
      localStorage.setItem(`lg_user_${parsed.phone}`, JSON.stringify(verifiedUser));
      localStorage.setItem('lg_user', JSON.stringify(verifiedUser)); // backward compat for other pages
      localStorage.setItem('lg_last_login', parsed.phone);
      sessionStorage.setItem('lg_active_phone', parsed.phone);
      setUser(verifiedUser);

      // Update last login info on session restore too
      try {
        const ua = navigator.userAgent || '';
        await updateDoc(doc(db, 'users', parsed.phone), {
          lastLogin: new Date().toISOString(),
          deviceInfo: ua.slice(0, 200),
          lastLoginDevice: parseDevice(ua),
        });
      } catch (e) {}
    } catch (err) {
      try {
        const raw = saved || finalSaved;
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && parsed.phone) {
            // Offline — check expiry locally
            if (parsed.sessionExpiry && new Date(parsed.sessionExpiry) < new Date()) {
              localStorage.removeItem(`lg_user_${parsed.phone}`);
              localStorage.removeItem('lg_user');
              sessionStorage.removeItem('lg_active_phone');
            } else {
              sessionStorage.setItem('lg_active_phone', parsed.phone);
              setUser(parsed);
            }
          }
        }
      } catch (e) {
        // corrupted data
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => { verifyUser(); }, [verifyUser]);

  // Session monitoring - periodic check instead of real-time onSnapshot
  // onSnapshot was causing continuous reads even when idle
  useEffect(() => {
    if (!user?.phone || !user?.sessionId) return;
    if (user.phone === DEV_PHONE) return;
    if (user.role === 'admin') return;

    const checkSession = async () => {
      try {
        const snap = await getDoc(doc(db, 'users', user.phone));
        if (!snap.exists()) { localStorage.removeItem(`lg_user_${user.phone}`); localStorage.removeItem('lg_user'); sessionStorage.removeItem('lg_active_phone'); window.location.reload(); return; }
        const data = snap.data();
        if (data.activeSession && data.activeSession !== user.sessionId) {
          localStorage.removeItem(`lg_user_${user.phone}`);
          localStorage.removeItem('lg_user');
          sessionStorage.removeItem('lg_active_phone');
          window.location.reload();
        }
      } catch (e) {} // offline — skip
    };

    // Check immediately, then every 2 minutes (was real-time onSnapshot)
    checkSession();
    const id = setInterval(checkSession, 2 * 60 * 1000);
    return () => clearInterval(id);
  }, [user?.phone, user?.sessionId]);

  // Periodic session expiry check (every 5 min)
  useEffect(() => {
    if (!user) return;
    const check = () => {
      const phone = sessionStorage.getItem('lg_active_phone');
      if (!phone) return;
      const saved = localStorage.getItem(`lg_user_${phone}`);
      if (!saved) return;
      const { sessionExpiry } = JSON.parse(saved);
      if (sessionExpiry && new Date(sessionExpiry) < new Date()) {
        localStorage.removeItem(`lg_user_${phone}`);
        localStorage.removeItem('lg_user');
        sessionStorage.removeItem('lg_active_phone');
        window.location.reload();
      }
    };
    const id = setInterval(check, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [user]);

  const handleLogin = useCallback(async (u) => { const lastLoginTime = parseInt(sessionStorage.getItem('lg_last_login_time') || '0'); if (Date.now() - lastLoginTime < 3000) return; sessionStorage.setItem('lg_last_login_time', String(Date.now()));
    // Show success animation first, then set user
    setLoginSuccess({ name: u.name || '', role: u.role || 'retailer' });
    setTimeout(() => {
      setLoginSuccess(null);
      setUser(u);
    }, 1800);
    // Save device info on login
    try {
      if (u.phone) {
        const ua = navigator.userAgent || '';
        await updateDoc(doc(db, 'users', u.phone), {
          lastLogin: new Date().toISOString(),
          deviceInfo: ua.slice(0, 200),
          lastLoginDevice: parseDevice(ua),
        });
      }
    } catch (e) {}
  }, []);

  const router = useMemo(() => {
    if (!user) return null;
    const role = user.role;
    const isDev = user.phone === '8051725780';

    const homeRedirect = isDev ? '/dev' : role === 'admin' ? '/admin' : '/portal';

    return createBrowserRouter([
      {
        path: '/',
        element: <Navigate to={homeRedirect} replace />,
      },
      {
        path: '/portal',
        element: role === 'retailer' ? <AppErrorBoundary><RetailerLayout /></AppErrorBoundary> : <Navigate to={homeRedirect} replace />,
        children: role === 'retailer' ? [
          { index: true, element: <RetailerHome /> },
          { path: 'order', element: <PlaceOrder /> },
          { path: 'checkout', element: <Checkout /> },
          { path: 'track', element: <TrackOrder /> },
          { path: 'history', element: <OrderHistory /> },
          { path: 'my-ledger', element: <MyLedger /> },
          { path: 'profile', element: <RetailerProfile /> },
          { path: 'support', element: <Support /> },
          { path: 'settings', element: <Navigate to="/profile" replace /> },
          { path: 'prices', element: <PriceList /> },
          { path: 'about', element: <About /> },
          { path: 'privacy', element: <PrivacyPolicy /> },
          { path: 'terms', element: <Terms /> },
          { path: 'guide', element: <Navigate to="/support" replace /> },
        ] : [],
      },
      {
        element: role === 'retailer' ? <AppErrorBoundary><RetailerLayout /></AppErrorBoundary> : <Navigate to={homeRedirect} replace />,
        children: role === 'retailer' ? [
          { path: '/order', element: <PlaceOrder /> },
          { path: '/checkout', element: <Checkout /> },
          { path: '/track', element: <TrackOrder /> },
          { path: '/history', element: <OrderHistory /> },
          { path: '/my-ledger', element: <MyLedger /> },
          { path: '/profile', element: <RetailerProfile /> },
          { path: '/support', element: <Support /> },
          { path: '/settings', element: <Navigate to="/profile" replace /> },
          { path: '/prices', element: <PriceList /> },
          { path: '/about', element: <About /> },
          { path: '/privacy', element: <PrivacyPolicy /> },
          { path: '/terms', element: <Terms /> },
          { path: '/guide', element: <Navigate to="/support" replace /> },
        ] : [],
      },
      {
        path: '/admin',
        element: (role === 'admin' || isDev) ? <AppErrorBoundary><AdminLayout /></AppErrorBoundary> : <Navigate to={homeRedirect} replace />,
        children: (role === 'admin' || isDev) ? [
          { index: true, element: <AdminDashboard /> },
          { path: 'retailers', element: <AdminRetailers /> },
          { path: 'orders/:id', element: <OrderDetail /> },
          { path: 'inventory', element: <AdminInventory /> },
          { path: 'ledger', element: <AdminLedger /> },
          { path: 'settings', element: <AdminSettings /> },

          { path: 'daily-ledger', element: <DailyLedger /> },
          { path: 'company-order', element: <CompanyOrder /> },
          { path: 'support', element: <SupportTickets /> },
          { path: 'sessions', element: <AdminSessions /> },
          { path: 'announcements', element: <AdminAnnouncements /> },
          { path: 'place-order', element: <AdminPlaceOrder /> },
          { path: 'place_order', element: <AdminPlaceOrder /> },
          ] : [],
      },
      {
        path: '/dev',
        element: isDev ? <DevErrorBoundary><DeveloperLayout /></DevErrorBoundary> : <Navigate to={homeRedirect} replace />,
        children: isDev ? [
          { index: true, element: <DevPanel /> },
          { path: 'errors', element: <ErrorLogs /> },
          { path: 'sessions', element: <UserSessions /> },
          { path: 'analytics', element: <OrderAnalytics /> },
          { path: 'order-manager', element: <OrderManager /> },
          { path: 'activity', element: <RetailerActivity /> },
          { path: 'deploy', element: <DeployInfo /> },
          { path: 'announce', element: <Announcements /> },
          { path: 'cleanup', element: <DatabaseCleanup /> },
          { path: 'flags', element: <FeatureFlags /> },
          { path: 'audit', element: <AuditLog /> },
          { path: 'ratings', element: <AppRatings /> },
          { path: 'export', element: <DataExport /> },
          { path: 'backup', element: <BackupRestore /> },
          { path: 'api-monitor', element: <ApiResponseMonitor /> },
          { path: 'tasks', element: <ScheduledTasks /> },
          { path: 'config-diff', element: <ConfigDiff /> },
          { path: 'editor', element: <DocEditor /> },
          { path: 'bulk-update', element: <BulkUpdate /> },
          { path: 'report', element: <MonthlyReport /> },
          { path: 'guide', element: <DevGuide /> },
          ] : [],
      },
      { path: '/privacy', element: <AppErrorBoundary><PrivacyPolicy /></AppErrorBoundary> },
      { path: '/terms', element: <AppErrorBoundary><Terms /></AppErrorBoundary> },
      { path: '/login', element: <Navigate to={homeRedirect} replace /> },
      { path: '*', element: <NotFound /> },
    ]);
  // eslint-disable-next-line
  }, [user?.role, user?.phone]);

  const publicRouter = useMemo(() => {
    return createBrowserRouter([
      {
        path: '/',
        element: (
          <AppErrorBoundary>
            <Suspense fallback={<SplashScreen />}>
              <LandingPage />
            </Suspense>
          </AppErrorBoundary>
        ),
      },
      {
        path: '/login',
        element: (
          <AppErrorBoundary>
            <Login onLogin={handleLogin} />
            {loginSuccess && <LoginSuccess name={loginSuccess.name} role={loginSuccess.role} />}
          </AppErrorBoundary>
        ),
      },
      {
        path: '/dev',
        element: (
          <AppErrorBoundary>
            <Login onLogin={handleLogin} />
            {loginSuccess && <LoginSuccess name={loginSuccess.name} role={loginSuccess.role} />}
          </AppErrorBoundary>
        ),
      },
      {
        path: '/dev/*',
        element: <Navigate to="/dev" replace />,
      },
      {
        path: '/privacy',
        element: (
          <AppErrorBoundary>
            <Suspense fallback={<SplashScreen />}>
              <PrivacyPolicy />
            </Suspense>
          </AppErrorBoundary>
        ),
      },
      {
        path: '/terms',
        element: (
          <AppErrorBoundary>
            <Suspense fallback={<SplashScreen />}>
              <Terms />
            </Suspense>
          </AppErrorBoundary>
        ),
      },
      {
        path: '/about',
        element: (
          <AppErrorBoundary>
            <Suspense fallback={<SplashScreen />}>
              <About />
            </Suspense>
          </AppErrorBoundary>
        ),
      },
      { path: '*', element: <Navigate to="/" replace /> },
    ]);
  }, [handleLogin, loginSuccess]);

  // Site block (from Dev Console or Firebase) — only developer can bypass, blocks all visitors INSTANTLY (zero delay)
  if (siteBlock && (!user || user.phone !== DEV_PHONE) && !isDevAccess) {
    if (siteBlock.type === 'vercel' || siteBlock.style === 'vercel' || !user) {
      return <DeploymentDisabled />;
    }
    return <PaymentBlock config={siteBlock} userRole={user.role} />;
  }

  if (loading || !splashDone) return <SplashScreen />;

  if (!user) {
    return (
      <>
        <TopProgressBar />
        <RouterProvider router={publicRouter} />
        <OfflineBanner />
      </>
    );
  }

  if (maintenance && user.role === 'retailer' && user.phone !== DEV_PHONE) {
    return (
      <MaintenancePage
        message={maintenanceInfo.message}
        estimatedUptime={maintenanceInfo.estimatedUptime}
        shopPhone={maintenanceInfo.shopPhone}
      />
    );
  }

  // Blocked retailer — only Blocked page + Support
  if (user.role === 'retailer' && user.blocked && user.phone !== DEV_PHONE) {
    const blockedRouter = createBrowserRouter([
      {
        path: '/',
        element: (
          <AppErrorBoundary>
            <Suspense fallback={<SplashScreen />}>
              <LandingPage />
            </Suspense>
          </AppErrorBoundary>
        ),
      },
      {
        path: '/portal',
        element: <AppErrorBoundary><RetailerLayout /></AppErrorBoundary>,
        children: [
          { index: true, element: <Blocked /> },
          { path: 'support', element: <Support /> },
          { path: '*', element: <Navigate to="/portal" replace /> },
        ],
      },
      {
        element: <AppErrorBoundary><RetailerLayout /></AppErrorBoundary>,
        children: [
          { path: '/support', element: <Support /> },
          { path: '*', element: <Navigate to="/portal" replace /> },
        ],
      },
      { path: '/login', element: <Navigate to="/portal" replace /> },
      { path: '*', element: <Navigate to="/portal" replace /> },
    ]);
    return (
      <>
        <TopProgressBar />
        <RouterProvider router={blockedRouter} />
        <OfflineBanner />
      </>
    );
  }

  return (
    <>
      <TopProgressBar />
      <RouterProvider router={router} />
      <InstallPrompt />
      <OfflineBanner />
      {user.role === 'retailer' && <WelcomePopup />}
    </>
  );
}

export default function App() {
  return (
    <ConfirmProvider>
      <FeatureFlagProvider>
        <AppContent />
      </FeatureFlagProvider>
    </ConfirmProvider>
  );
}



